import styled from "@emotion/styled";
import { TFormattedAttendee, TReservation, updateMeetingReservation } from "apis/meetings/meeting-reservation";
import { useEffect, useRef, useState } from "react";
import UI from "@mile-meeting/mile-ui";
import {
  Title as TitleInput,
  DateInput,
  Time as TimeInput,
  MeetingRoom as MeetingRoomInput,
  Attendees as AttendeesInput,
  Memo as MemoInput,
  ReservationConfidential,
  EditPermissionCheckbox,
  RepeatReservationBtn,
  RepeatCondition,
} from "../booking-list/reservation-modal/input-forms";
import { getMeetingRoomList, TMeetingRoom } from "apis/meetings/meeting-room";
import { useToast } from "hook/useToast";
import { extractHtmlText, sleep } from "utils/validation";
import { LoadingModal } from "uis/modal";
import { useRecoilValue } from "recoil";
import { member, workspace } from "recoil/recoilState";
import { RepeatReservationConditionalOption } from "uis/modal/repeat-edit-conditinal-modal";
import { UtilsService } from "utils/utils.service";
import { RRuleService } from "utils/rrule.service";
import { TRRule } from "uis/modal/repeat-rule-setting-modal/types";
import { services } from "../booking-list/edit-modal/service";
import * as Modal from "uis/modal";
import { AmplitudeService } from "utils/amplitude.service";

type RsvModifySideModalProps = {
  open?: boolean;
  setOpen?: (value: boolean) => void;
  canEdit?: boolean;
  reservation?: TReservation;
  onUpdate?: () => void;
  editOption?: RepeatReservationConditionalOption;
};

//INFO: 수정하기 버튼이 활성화 될 수 있는 조건
const enableReservation = (
  title: string,
  attendees: TFormattedAttendee[],
  minAttendeeLimit: number,
  isAllowViolateMinAttendeeLimit: boolean,
  meetingRoom?: TMeetingRoom
) => {
  if (attendees.length < minAttendeeLimit && !isAllowViolateMinAttendeeLimit) return false;
  return title?.length > 0 && meetingRoom && attendees.length > 0;
};

const RsvModifySideModal = (props: RsvModifySideModalProps) => {
  const utilsService = new UtilsService();
  const rruleService = new RRuleService(utilsService);
  const amplitudeService = new AmplitudeService();
  const workspaceInfo = useRecoilValue(workspace);

  const { showToast } = useToast();
  const { open, setOpen, canEdit, reservation, onUpdate, editOption } = props;
  const ref = useRef<HTMLDivElement>(null);
  const today = new Date();
  const after30Minutes = new Date();

  const [title, setTitle] = useState<string>();
  const [date, setDate] = useState<Date>(new Date());
  const [meetingRooms, setMeetingRooms] = useState<TMeetingRoom[]>([]);
  const [startTime, setStartTime] = useState<{ hour: number; minutes: number }>({
    hour: today.getMinutes() > 30 ? today.getHours() + 1 : today.getHours(),
    minutes: today.getMinutes() === 0 ? 0 : today.getMinutes() < 30 ? 30 : 0,
  });
  const [endTime, setEndTime] = useState<{ hour: number; minutes: number }>({
    hour: after30Minutes.getMinutes() > 30 ? after30Minutes.getHours() + 1 : after30Minutes.getHours(),
    minutes: after30Minutes.getMinutes() === 0 ? 0 : after30Minutes.getMinutes() < 30 ? 30 : 0,
  });
  const [meetingRoom, setMeetingRoom] = useState<TMeetingRoom>();
  const [attendees, setAttendees] = useState<TFormattedAttendee[]>([]);
  const [memo, setMemo] = useState<string>();
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [guestCanModify, setGuestCanModify] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [rrule, setRrule] = useState<TRRule | undefined>();
  const [pastRecurrenceCount, setPastRecurrenceCount] = useState<number | undefined>(undefined);
  const [availableReservation, setAvailableReservation] = useState<string[]>([]);
  const [unavailableReservation, setUnavailableReservation] = useState<string[]>([]);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState<boolean>(false);
  const [showAllDuplicateAlert, setShowAllDuplicateAlert] = useState(false);
  const showDuplicateAlertRef = useRef<boolean>(false);
  const showAllDuplicateAlertRef = useRef<boolean>(false);
  const [minAttendeeLimit, setMinAttendeeLimit] = useState<number>(1);
  const [isAllowViolateMinAttendeeLimit, setIsAllowViolateMinAttendeeLimit] = useState<boolean>(false);

  const memberInfo = useRecoilValue(member);

  //* INFO : 최소 참석 제한에 막혀 예약을 하지 못하는 경우 확인
  const checkMinAttendeeLimit = () => {
    if (title && title?.length > 0 && meetingRoom && attendees.length > 0) {
      if (attendees.length < minAttendeeLimit && !isAllowViolateMinAttendeeLimit) {
        //* INFO: 최소 참석 제한에 막혀 예약을 하지 못하는 경우
        amplitudeService.logPeakTimeMinAttendanceReservationBlock(
          workspaceInfo?.id ?? "",
          workspaceInfo?.name ?? "",
          meetingRoom?.id ?? "",
          meetingRoom?.name ?? "",
          "수정",
          minAttendeeLimit,
          memberInfo?.id ?? "",
          memberInfo?.email ?? "",
          attendees.map((item) => item.id ?? "")
        );
      }
      if (attendees.length < minAttendeeLimit && isAllowViolateMinAttendeeLimit) {
        //* INFO: 최소 참석 위반 예약 생성시
        amplitudeService.logMinAttendanceExceptionReservation(
          workspaceInfo?.id ?? "",
          workspaceInfo?.name ?? "",
          meetingRoom?.id ?? "",
          meetingRoom?.name ?? "",
          "수정",
          minAttendeeLimit,
          memberInfo?.id ?? "",
          memberInfo?.email ?? "",
          attendees.map((item) => item.id ?? "")
        );
      }
    }
  };

  const onHandleEdit = async () => {
    //* INFO: 최소 참석 제한관련 로그
    checkMinAttendeeLimit();

    const startRsvTime = new Date(date);
    startRsvTime.setHours(startTime.hour, startTime.minutes, 0, 0);

    const endRsvTime = new Date(date);
    endRsvTime.setHours(endTime.hour, endTime.minutes, 0, 0);

    if (loading) return;
    setLoading(true);

    if (rrule && editOption === RepeatReservationConditionalOption.THIS_SCHEDULE_AND_FUTURE_SCHEDULES) {
      //* INFO: 이 예약 및 향후 예약 수정 시, 이전 일정이 있는지 확인
      const res = await services.checkRecurringReservationDuplicate(
        date,
        startRsvTime,
        endRsvTime,
        rruleService.generateRRuleString(rrule, date),
        meetingRoom?.id ?? "",
        editOption,
        reservation?.instanceId
      );
      if (res?.available.length === 0 && res.unavailable.length > 0) {
        //* INFO: 이전 일정이 있는 경우
        setShowDuplicateAlert(true);
        setLoading(false);
        return;
      } else if (res?.unavailable && res?.unavailable.length > 0) {
        //* INFO: 중복되는 예약이 있는 경우 (일부)
        setAvailableReservation(res.available);
        setUnavailableReservation(res.unavailable);
        setShowDuplicateAlert(true);
        setLoading(false);
        return;
      } else {
        //* INFO: 중복되는 예약이 없는 경우
        await editReservation();
        setLoading(false);
        return;
      }
    }
    if (rrule && editOption === RepeatReservationConditionalOption.ALL_SCHEDULES) {
      //* INFO: 모든 예약 수정 시, 중복된 예약이 있는지 확인
      const res = await services.checkRecurringReservationDuplicate(
        new Date(reservation?.recurringStartDate ?? ""),
        startRsvTime ?? new Date(),
        endRsvTime ?? new Date(),
        rruleService.generateRRuleString(rrule, new Date(date ?? "")),
        meetingRoom?.id ?? "",
        editOption,
        reservation?.instanceId
      );

      if (res?.available.length === 0 && res.unavailable.length > 0) {
        //* INFO: 모든 예약이 중복되는 경우
        setShowAllDuplicateAlert(true);
        setLoading(false);
        return;
      } else if (res?.unavailable && res?.unavailable.length > 0) {
        //* INFO: 중복되는 예약이 있는 경우 (일부)
        setAvailableReservation(res.available);
        setUnavailableReservation(res.unavailable);
        setShowDuplicateAlert(true);
        setLoading(false);
        return;
      } else {
        //* INFO: 중복되는 예약이 없는 경우
        await editReservation();
        setLoading(false);
        return;
      }
    } else {
      await editReservation();
      setLoading(false);
    }
  };

  const editReservation = async () => {
    let requestMemo = memo ?? "";
    if (
      editOption === RepeatReservationConditionalOption.THIS_SCHEDULE && reservation?.googleEventId &&
      reservation.meetingRoom.id === meetingRoom?.id &&
      meetingRoom?.meetingRoomIntegration?.integrationProvider === "GOOGLE"
    ) {
      const original = new DOMParser().parseFromString(reservation.memo ?? "", "text/html");
      const metadata = original.createElement("data");
      original.querySelectorAll("data").forEach((data) => {
        Array.from(data.attributes).forEach((attr) => metadata.setAttribute(attr.name, attr.value));
      });
      if (metadata.hasAttribute("creatorEmail")) {
        metadata.textContent = requestMemo;
        requestMemo = metadata.outerHTML;
      }
    }
    if (meetingRoom?.meetingRoomIntegration?.integrationProvider === "GOOGLE") {
      await sleep(4000);
    }
    const startRsvTime = new Date(date);
    startRsvTime.setHours(startTime.hour, startTime.minutes, 0, 0);

    const endRsvTime = new Date(date);
    endRsvTime.setHours(endTime.hour, endTime.minutes, 0, 0);

    let option =
      editOption === RepeatReservationConditionalOption.ALL_SCHEDULES
        ? "ALL"
        : editOption === RepeatReservationConditionalOption.THIS_SCHEDULE
          ? "THIS"
          : editOption === RepeatReservationConditionalOption.THIS_SCHEDULE_AND_FUTURE_SCHEDULES
            ? "THIS_AND_FUTURE"
            : undefined;

    const res = await updateMeetingReservation({
      id: reservation?.id ?? "",
      title: title ?? "",
      memo: requestMemo,
      isPrivate: isPrivate,
      guestsCanModify: guestCanModify,
      rsvStartTime: startRsvTime.toISOString(),
      rsvEndTime: endRsvTime.toISOString(),
      beforeMeetingRoomId: reservation?.meetingRoom?.id ?? "",
      afterMeetingRoomId: meetingRoom?.id ?? "",
      googleEventId: reservation?.googleEventId,
      reservationAttendees: attendees.map((attendee) => {
        return {
          isVisitor: attendee.isVisitor,
          visitorEmail: attendee.email,
          memberId: attendee.id,
        };
      }),
      rrule: rrule ? rruleService.generateRRuleString(rrule, date) : undefined,
      editOption: option,
      exceptedAttendeeLimit: isAllowViolateMinAttendeeLimit,
    });
    if (res.success) {
      showToast("회의실 예약이 수정되었습니다.", "success");
      setOpen?.(false);
      onUpdate?.();
    } else {
      showToast(
        res.message ?? "회의실 예약 수정이 실패되었습니다. 다시 시도해주세요.",
        res.status === 409 ? "warning" : "error"
      );
    }
  };

  useEffect(() => {
    if (open) {
      getMeetingRoomList(1, [], [], 50).then((res) => {
        setMeetingRooms(res.data?.meetingRoomList ?? []);
      });
    }

    const handler = (event: any) => {
      if (open && ref.current && !ref.current.contains(event.target) && !showDuplicateAlertRef.current) {
        setOpen?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  useEffect(() => {
    showDuplicateAlertRef.current = showDuplicateAlert;
    showAllDuplicateAlertRef.current = showAllDuplicateAlert;
  }, [showDuplicateAlert, showAllDuplicateAlert]);

  useEffect(() => {
    if (!reservation) return;

    const rsvStartTime = reservation?.startTime ? new Date(reservation?.startTime) : new Date();
    const rsvEndTime = new Date(reservation?.endTime ?? "");
    const transformedAttendee = (reservation?.reservationAttendees ?? []).map((item) => ({
      isVisitor: item.isVisitor,
      email: item.isVisitor ? item.visitorEmail : item.member.email,
      picture: item.isVisitor ? undefined : item.member.image,
      id: item.isVisitor ? undefined : item.member.id,
      name: item.isVisitor ? undefined : item.member.nickname,
    }));

    const rrule: TRRule | undefined = reservation.rrule ? rruleService.parseRRuleString(reservation.rrule) : undefined;

    setRrule(rrule);
    setTitle(reservation?.title ?? "");
    setDate(rsvStartTime);
    setStartTime({ hour: rsvStartTime.getHours(), minutes: rsvStartTime.getMinutes() });
    setEndTime({ hour: rsvEndTime.getHours(), minutes: rsvEndTime.getMinutes() });
    setGuestCanModify(reservation.guestsCanModify);
    setMeetingRoom(reservation?.meetingRoom);
    setAttendees(transformedAttendee);
    setMemo(extractHtmlText(reservation?.memo ?? "").text);
    setIsPrivate(reservation.isPrivate);

    //* INFO: 이미 위반된 예약이라면, 위반 허용을 다시 물어보지 않도록 설정
    const minAttendeeLimit = services.getAttendeeLimit(
      meetingRoom?.attendeeLimits ?? [],
      new Date(new Date().setHours(startTime.hour, startTime.minutes)),
      new Date(new Date().setHours(endTime.hour, endTime.minutes))
    );
    setMinAttendeeLimit(minAttendeeLimit);

    if (attendees.length < minAttendeeLimit) {
      setIsAllowViolateMinAttendeeLimit(true);
    } else {
      setIsAllowViolateMinAttendeeLimit(false);
    }
  }, [open]);

  useEffect(() => {
    if (!endTime || !startTime || !meetingRoom) return;
    const sameEndTime =
      endTime.hour === new Date(reservation?.endTime ?? "").getHours() &&
      endTime.minutes === new Date(reservation?.endTime ?? "").getMinutes();
    const sameStartTime =
      startTime.hour === new Date(reservation?.startTime ?? "").getHours() &&
      startTime.minutes === new Date(reservation?.startTime ?? "").getMinutes();
    const sameMeetingRoom = meetingRoom?.id === reservation?.meetingRoom?.id;

    const minAttendeeLimit = services.getAttendeeLimit(
      meetingRoom?.attendeeLimits ?? [],
      new Date(new Date().setHours(startTime.hour, startTime.minutes)),
      new Date(new Date().setHours(endTime.hour, endTime.minutes))
    );

    if (sameEndTime && sameStartTime && sameMeetingRoom) {
      //* INFO: 기존 예약과 동일할 경우, 위반 허용을 다시 물어보지 않도록 설정
      setIsAllowViolateMinAttendeeLimit(true);
      setMinAttendeeLimit(minAttendeeLimit);
      return;
    } else {
      setMinAttendeeLimit(minAttendeeLimit);
      //* INFO: 회의실이 바뀌거나, 시간이 바뀌면 기존에 예약 위반된 회의더라도, 다시 위반 모달을 띄우도록 설정
      setIsAllowViolateMinAttendeeLimit(false);
    }
  }, [meetingRoom, startTime, endTime]);

  return (
    <Container style={{ display: open ? "flex" : "none" }} ref={ref}>
      <div style={{ position: "absolute", top: 20, right: 20 }}>
        <UI.IconButton.Normal
          type="primary"
          icon="x"
          size={20}
          color="var(--alias-color-label-normal)"
          onClick={() => setOpen?.(false)}
        />
      </div>
      <ContentContainer>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-neutral)"
          style={{ padding: "0 40px" }}
        >
          예약 내역 수정
        </UI.Typography>
        <InputContainer>
          <BasicContainer>
            <TitleInput onChange={setTitle} value={title} />
            <div style={{ display: "flex", gap: 8, flexDirection: "column", width: "100%" }}>
              <DateInput value={date} onChange={setDate} />
              <div style={{ display: "flex", gap: 8, width: "100%" }}>
                <TimeInput
                  isToday={date.toDateString() === today.toDateString()}
                  startValue={startTime}
                  onChangeStart={(hour, minutes) => setStartTime({ hour: hour, minutes: minutes })}
                  endValue={endTime}
                  onChangeEnd={(hour, minutes) => setEndTime({ hour: hour, minutes: minutes })}
                />
              </div>
            </div>
            <RepeatCondition
              rrule={rrule}
              onChange={setRrule}
              selectedDate={date}
              editOption={editOption}
              minCount={pastRecurrenceCount}
              reservationDate={reservation?.startTime ? new Date(reservation?.startTime) : undefined}
            />
            <MeetingRoomInput
              open={open}
              meetingRooms={meetingRooms}
              onChange={setMeetingRoom}
              value={meetingRoom}
              attendeeCount={attendees.length}
            />
            <AttendeesInput
              open={open}
              value={attendees}
              onChange={setAttendees}
              minAttendeeLimit={minAttendeeLimit}
              isAllowViolateMinAttendeeLimit={isAllowViolateMinAttendeeLimit}
            />
            <MemoInput value={memo ?? ""} onChange={setMemo} />
          </BasicContainer>
        </InputContainer>
        <CheckboxContainer>
          <ReservationConfidential value={isPrivate} onChange={setIsPrivate} />
          {reservation?.reservationCreator?.member.id === memberInfo?.id && (
            <EditPermissionCheckbox value={guestCanModify} onChange={setGuestCanModify} />
          )}
        </CheckboxContainer>
        <Modal.ReservationConflictModal
          closeModal={() => setShowDuplicateAlert(false)}
          modalIsOpen={showDuplicateAlert}
          totalReservationCount={availableReservation.length + unavailableReservation.length}
          enableReservationCount={availableReservation.length}
          disableReservationCount={unavailableReservation.length}
          enableReservationTimes={unavailableReservation.map((time) => new Date(time))}
          onReservation={async (e) => {
            e.stopPropagation();
            setShowDuplicateAlert(false);
            setLoading(true);
            await editReservation();
            setLoading(false);
          }}
        />
        <Modal.ReservationAllConflictModal
          modalOpen={showAllDuplicateAlert}
          closeModal={() => setShowAllDuplicateAlert(false)}
        />
        <ButtonContainer>
          <UI.Button
            type="solid"
            title="수정하기"
            disabled={
              !enableReservation(title ?? "", attendees, minAttendeeLimit, isAllowViolateMinAttendeeLimit, meetingRoom)
            }
            size="large"
            style={{ width: "100%", flexShrink: 0 }}
            onClick={onHandleEdit}
          />
        </ButtonContainer>
      </ContentContainer>
      <LoadingModal modalIsOpen={loading} />
    </Container>
  );
};

export default RsvModifySideModal;

const Container = styled.div`
  display: flex;
  box-sizing: border-box;
  width: 480px;
  height: calc(100% - 64px);
  padding: 24px 0 40px 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 40px;
  box-sizing: border-box;

  position: fixed;
  right: 0px;
  top: 64px;
  z-index: 5;

  transition: all ease 0.3s;
  white-space: nowrap;

  background: var(--alias-color-background-normal-normal);
  box-shadow: var(--alias-style-shadow-heavy);
`;

const ContentContainer = styled.div`
  flex-grow: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
`;

const InputContainer = styled.div`
  padding: 0 40px;
  flex-grow: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
  overflow-y: auto;
`;

const BasicContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const ButtonContainer = styled.div`
  box-sizing: border-box;
  padding: 0 40px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CheckboxContainer = styled.div`
  padding: 0 40px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
